import React from 'react';
import { MacroDonutWithLegend } from '@/components/shared/MacroDonutWithLegend';
import { formatMacro } from '@/utils/gram-ml';

interface NutritionTotalsProps {
  totalMacros: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    saturatedFat: number;
    /** Grams alcohol per recipe total; donut uses carb/protein/fat/alcohol energy shares only. */
    alcohol: number;
  };
  servings: number;
}

function NutritionCard({
  title,
  values,
}: {
  title: string;
  values: NutritionTotalsProps['totalMacros'];
}) {
  return (
    <div
      className="flex w-full flex-shrink-0 flex-col gap-[9px] rounded-[10px] border border-black/10 bg-white px-6 py-2.5 shadow-[0_0_4px_rgba(0,0,0,0.04)]"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <h3 className="text-[12px] font-medium leading-[20px] text-black">{title}</h3>

      <MacroDonutWithLegend
        carbs={values.carbs}
        protein={values.protein}
        fat={values.fat}
        alcohol={values.alcohol}
        fourthEnergyNutrient="alcohol"
        fiber={0}
        fiberLabel="Fibre"
        centerPrimaryOverride={
          typeof values.calories === 'number' && Number.isFinite(values.calories) && values.calories > 0
            ? formatMacro(values.calories)
            : undefined
        }
      />
    </div>
  );
}

export const NutritionTotals: React.FC<NutritionTotalsProps> = ({
  totalMacros,
  servings,
}) => {
  const normalizedServings = Math.max(Number(servings) || 1, 1);
  const perServingMacros = {
    calories: totalMacros.calories / normalizedServings,
    protein: totalMacros.protein / normalizedServings,
    carbs: totalMacros.carbs / normalizedServings,
    fat: totalMacros.fat / normalizedServings,
    fiber: totalMacros.fiber / normalizedServings,
    saturatedFat: totalMacros.saturatedFat / normalizedServings,
    alcohol: totalMacros.alcohol / normalizedServings,
  };

  const cards =
    normalizedServings > 1
      ? [
          { key: 'per-serving', title: 'Nutrition (Per Serving)', values: perServingMacros },
          {
            key: 'total',
            title: `Nutrition (Total for ${normalizedServings} servings)`,
            values: totalMacros,
          },
        ]
      : [{ key: 'single', title: 'Nutrition (Per Serving)', values: perServingMacros }];

  return (
    <div className="w-full shrink-0 lg:w-auto lg:min-w-[286px]">
      <div className="sticky top-6 flex flex-col gap-4">
        {cards.map((card) => (
          <NutritionCard key={card.key} title={card.title} values={card.values} />
        ))}
      </div>
    </div>
  );
};
